import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import {
    Users, Search, Mail, Phone, MessageSquare, AlertCircle,
    CheckCircle2, X, Send, Filter, RefreshCw, Hash, Calendar,
    Briefcase, FileText, Star, Megaphone, ShieldCheck, Loader2,
} from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const SOURCE_META = {
    users: { label: "Account", icon: ShieldCheck, color: "text-fiji-green" },
    newsletter_subscribers: { label: "Bula Brief", icon: Megaphone, color: "text-coral" },
    investor_watchlist: { label: "Investor Watchlist", icon: Star, color: "text-coral" },
    jv_leads: { label: "Lead/Consultation", icon: FileText, color: "text-ocean dark:text-turquoise" },
    business_nda_requests: { label: "NDA Request", icon: Briefcase, color: "text-coral-deep" },
};

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString();
    } catch { return iso; }
}

export default function AdminContactsTab() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState("");
    const [sourceFilter, setSourceFilter] = useState("all");
    const [drawerEmail, setDrawerEmail] = useState(null);

    const fetchContacts = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (sourceFilter !== "all") params.set("source", sourceFilter);
            params.set("limit", "200");
            const { data } = await api.get(`/admin/contacts?${params.toString()}`);
            setData(data);
        } catch (e) {
            toast.error("Failed to load contacts");
        } finally {
            setLoading(false);
        }
    }, [q, sourceFilter]);

    useEffect(() => {
        const id = setTimeout(fetchContacts, 250);
        return () => clearTimeout(id);
    }, [fetchContacts]);

    const stats = data?.stats;
    const items = data?.items || [];
    const smsEnabled = data?.twilio_sms_enabled;

    return (
        <div data-testid="contacts-tab" className="space-y-4">
            {/* Header / stats */}
            <Card className="p-5 bg-gradient-to-br from-ocean/5 to-coral/5 dark:from-ocean/15 dark:to-coral/10 border-coral/20">
                <div className="flex items-start gap-3 mb-4">
                    <Users className="h-6 w-6 text-coral mt-0.5 shrink-0" />
                    <div className="flex-1">
                        <h3 className="font-display text-lg font-bold text-foreground mb-1">Unified contacts directory</h3>
                        <p className="font-app text-sm text-muted-foreground">
                            Every name, email, and phone captured anywhere — registrations, Bula Brief, investor watchlist, leads/consultations, and NDA requests — deduped by email. Click any row for full history + send SMS.
                        </p>
                    </div>
                </div>
                {stats && (
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        <div className="bg-card border border-border rounded-lg p-3" data-testid="contacts-stat-total">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Unique</div>
                            <div className="font-display text-2xl font-bold tabular-nums text-foreground">{stats.total_unique}</div>
                        </div>
                        <div className="bg-card border border-border rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">w/ Phone</div>
                            <div className="font-display text-2xl font-bold tabular-nums text-foreground">{stats.with_phone}</div>
                        </div>
                        <div className="bg-card border border-border rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Accounts</div>
                            <div className="font-display text-2xl font-bold tabular-nums text-foreground">{stats.with_account}</div>
                        </div>
                        <div className="bg-card border border-border rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">Leads</div>
                            <div className="font-display text-2xl font-bold tabular-nums text-foreground">{stats.by_source.jv_leads}</div>
                        </div>
                        <div className="bg-card border border-border rounded-lg p-3">
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-app">NDAs</div>
                            <div className="font-display text-2xl font-bold tabular-nums text-foreground">{stats.by_source.business_nda_requests}</div>
                        </div>
                    </div>
                )}
            </Card>

            {/* SMS sender warning */}
            {data && !smsEnabled && (
                <Card className="p-4 border-coral/40 bg-coral/5 dark:bg-coral/10" data-testid="sms-disabled-banner">
                    <div className="flex items-start gap-3">
                        <AlertCircle className="h-5 w-5 text-coral mt-0.5 shrink-0" />
                        <div>
                            <div className="font-app font-semibold text-sm text-foreground mb-1">Outbound SMS not yet active</div>
                            <p className="font-app text-xs text-muted-foreground">
                                Twilio Verify (used for OTP) is connected, but to send arbitrary SMS to contacts we need a sender. Add either{" "}
                                <code className="bg-secondary/60 px-1.5 py-0.5 rounded text-coral text-[11px]">TWILIO_FROM_NUMBER</code> (a Twilio number in E.164 format) or{" "}
                                <code className="bg-secondary/60 px-1.5 py-0.5 rounded text-coral text-[11px]">TWILIO_MESSAGING_SERVICE_SID</code> to <code className="text-[11px]">/app/backend/.env</code> and restart backend.
                                Buy a Twilio number at <span className="underline">twilio.com/console/phone-numbers/search</span> (~US$1/month + per-message cost).
                            </p>
                        </div>
                    </div>
                </Card>
            )}

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        data-testid="contacts-search"
                        placeholder="Search name, email, or phone…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 bg-card text-foreground placeholder:text-muted-foreground"
                    />
                </div>
                <Select value={sourceFilter} onValueChange={setSourceFilter}>
                    <SelectTrigger data-testid="contacts-source-filter" className="sm:w-56 bg-card text-foreground">
                        <Filter className="h-4 w-4 mr-2" />
                        <SelectValue placeholder="All sources" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All sources</SelectItem>
                        {Object.entries(SOURCE_META).map(([k, m]) => (
                            <SelectItem key={k} value={k}>{m.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchContacts} disabled={loading} data-testid="contacts-refresh-btn" className="shrink-0">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                </Button>
            </div>

            {/* Contact list */}
            {data === null ? (
                <Card className="p-12 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-coral" /></Card>
            ) : items.length === 0 ? (
                <Card className="p-12 text-center" data-testid="contacts-empty">
                    <Users className="h-10 w-10 text-muted-foreground mx-auto mb-2 opacity-40" />
                    <p className="font-app text-sm text-muted-foreground">No contacts match your filter.</p>
                </Card>
            ) : (
                <Card className="overflow-hidden p-0">
                    <div className="divide-y divide-border">
                        {items.map((c) => (
                            <ContactRow key={c.email} contact={c} onClick={() => setDrawerEmail(c.email)} />
                        ))}
                    </div>
                    <div className="p-3 text-center text-xs text-muted-foreground font-app border-t border-border bg-secondary/30">
                        Showing {items.length} of {data?.total} contacts
                    </div>
                </Card>
            )}

            {drawerEmail && (
                <ContactDetailDialog
                    email={drawerEmail}
                    onClose={() => setDrawerEmail(null)}
                    onAfterSend={fetchContacts}
                />
            )}
        </div>
    );
}

function ContactRow({ contact, onClick }) {
    const primaryName = contact.names[0] || "—";
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={`contact-row-${contact.email}`}
            className="w-full text-left bg-card hover:bg-secondary/40 dark:hover:bg-secondary/30 transition px-4 py-3 flex items-center gap-3"
        >
            <div className="h-10 w-10 rounded-full bg-coral/15 grid place-items-center shrink-0">
                <span className="font-app font-bold text-sm text-coral">
                    {(primaryName[0] || contact.email[0] || "?").toUpperCase()}
                </span>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-app font-semibold text-sm text-foreground truncate">{primaryName}</span>
                    {contact.has_account && (
                        <ShieldCheck className="h-3.5 w-3.5 text-fiji-green shrink-0" title="Has registered account" />
                    )}
                </div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground font-app">
                    <span className="flex items-center gap-1 truncate"><Mail className="h-3 w-3 shrink-0" />{contact.email}</span>
                    {contact.phones[0] && (
                        <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phones[0]}</span>
                    )}
                </div>
            </div>
            <div className="hidden sm:flex flex-wrap gap-1 max-w-[40%] justify-end">
                {contact.sources.map((s) => {
                    const m = SOURCE_META[s];
                    if (!m) return null;
                    const Icon = m.icon;
                    return (
                        <Badge key={s} variant="outline" className="text-[10px] px-1.5 py-0 h-5 font-app">
                            <Icon className={`h-2.5 w-2.5 mr-1 ${m.color}`} />
                            {m.label}
                        </Badge>
                    );
                })}
            </div>
            <span className="text-[10px] text-muted-foreground font-app tabular-nums shrink-0 hidden md:inline">
                {fmtDate(contact.last_seen)}
            </span>
        </button>
    );
}

function ContactDetailDialog({ email, onClose, onAfterSend }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [smsBody, setSmsBody] = useState("");
    const [smsToPhone, setSmsToPhone] = useState("");
    const [sending, setSending] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/contacts/${encodeURIComponent(email)}`);
            setData(data);
            // Default phone: first known phone
            if (!smsToPhone && data.contact?.phones?.[0]) {
                setSmsToPhone(data.contact.phones[0]);
            }
        } catch (e) {
            toast.error("Failed to load contact");
            onClose();
        } finally {
            setLoading(false);
        }
    }, [email, onClose, smsToPhone]);

    useEffect(() => { refresh(); }, [email]);  // eslint-disable-line react-hooks/exhaustive-deps

    const sendSms = async () => {
        if (!smsBody.trim()) {
            toast.error("Message body required");
            return;
        }
        setSending(true);
        try {
            const body = { body: smsBody };
            if (smsToPhone) body.to_phone = smsToPhone;
            const { data: result } = await api.post(`/admin/contacts/${encodeURIComponent(email)}/sms`, body);
            toast.success(`SMS sent to ${result.to_phone} (Twilio status: ${result.twilio_status || result.status})`);
            setSmsBody("");
            await refresh();
            onAfterSend?.();
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "SMS send failed");
        } finally {
            setSending(false);
        }
    };

    const [convertingPipeline, setConvertingPipeline] = useState(false);
    const convertToPipeline = async () => {
        if (!data?.contact) return;
        setConvertingPipeline(true);
        try {
            const fullName = data.contact.names?.[0] || "";
            const { data: pipeline } = await api.post("/admin/investor-pipelines", {
                email,
                full_name: fullName || undefined,
                source: "contacts_tab_convert",
                note: `Converted from Contacts tab on ${new Date().toISOString().split("T")[0]}.`,
            });
            const isExisting = pipeline.created_at && pipeline.updated_at && pipeline.created_at !== pipeline.updated_at;
            toast.success(isExisting
                ? "Existing pipeline updated — open the Investor Pipelines tab to track."
                : "Pipeline created — open the Investor Pipelines tab to track.");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not create pipeline");
        } finally {
            setConvertingPipeline(false);
        }
    };

    const c = data?.contact;
    const messages = data?.messages || [];
    const smsEnabled = data?.twilio_sms_enabled;
    const charCount = smsBody.length;
    const segments = Math.max(1, Math.ceil(charCount / 160));

    return (
        <Dialog open={true} onOpenChange={(open) => !open && onClose()}>
            <DialogContent data-testid="contact-detail-dialog" className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="font-display text-foreground">{c?.names?.[0] || email}</DialogTitle>
                    <DialogDescription className="font-app text-muted-foreground">
                        Full contact history across every form & signup path.
                    </DialogDescription>
                </DialogHeader>

                {loading || !c ? (
                    <div className="py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-coral mx-auto" /></div>
                ) : (
                    <div className="space-y-4">
                        {/* Identity card */}
                        <div className="bg-secondary/40 dark:bg-secondary/30 rounded-lg p-4 border border-border">
                            <div className="grid sm:grid-cols-2 gap-3 text-sm font-app">
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Email</Label>
                                    <div className="text-foreground flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-coral" />{c.email}</div>
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Phones</Label>
                                    <div className="text-foreground">
                                        {c.phones.length === 0 ? (
                                            <span className="italic text-muted-foreground">none on file</span>
                                        ) : (
                                            c.phones.map((p) => (
                                                <div key={p} className="flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-coral" />{p}</div>
                                            ))
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Names on record</Label>
                                    <div className="text-foreground">{c.names.length === 0 ? "—" : c.names.join(" / ")}</div>
                                </div>
                                <div>
                                    <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">First / Last seen</Label>
                                    <div className="text-foreground text-xs">{fmtDateTime(c.first_seen)}<br />{fmtDateTime(c.last_seen)}</div>
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1.5 mt-3">
                                {c.sources.map((s) => {
                                    const m = SOURCE_META[s];
                                    if (!m) return null;
                                    const Icon = m.icon;
                                    return (
                                        <Badge key={s} variant="outline" className="font-app text-[10px]">
                                            <Icon className={`h-2.5 w-2.5 mr-1 ${m.color}`} />
                                            {m.label}
                                        </Badge>
                                    );
                                })}
                            </div>
                            <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center gap-2">
                                <Button
                                    onClick={convertToPipeline}
                                    disabled={convertingPipeline}
                                    size="sm"
                                    data-testid="contact-convert-pipeline-btn"
                                    className="bg-coral hover:bg-coral-deep text-white font-app"
                                >
                                    {convertingPipeline ? (
                                        <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                                    ) : (
                                        <Briefcase className="h-3.5 w-3.5 mr-1.5" />
                                    )}
                                    {convertingPipeline ? "Converting…" : "Convert to investor pipeline"}
                                </Button>
                                <span className="text-xs font-app text-muted-foreground">
                                    Creates the 12-step pipeline (idempotent — already-existing pipelines just get updated).
                                </span>
                            </div>
                        </div>

                        {/* Send SMS */}
                        <div className="bg-card border border-border rounded-lg p-4" data-testid="sms-compose">
                            <div className="flex items-center gap-2 mb-3">
                                <MessageSquare className="h-4 w-4 text-coral" />
                                <h4 className="font-display font-bold text-sm text-foreground">Send SMS via Twilio</h4>
                                {smsEnabled ? (
                                    <Badge className="bg-fiji-green/15 text-fiji-green border-fiji-green/30 text-[10px] uppercase">Ready</Badge>
                                ) : (
                                    <Badge className="bg-coral/15 text-coral border-coral/30 text-[10px] uppercase">Not configured</Badge>
                                )}
                            </div>

                            {!smsEnabled && (
                                <div className="text-xs text-muted-foreground font-app mb-3 bg-secondary/40 rounded p-2.5">
                                    Add <code className="text-coral">TWILIO_FROM_NUMBER</code> or{" "}
                                    <code className="text-coral">TWILIO_MESSAGING_SERVICE_SID</code> to backend env to enable.
                                </div>
                            )}

                            <div className="space-y-2.5">
                                <div>
                                    <Label className="text-xs font-app text-muted-foreground">To phone (E.164)</Label>
                                    <Input
                                        data-testid="sms-to-phone"
                                        value={smsToPhone}
                                        onChange={(e) => setSmsToPhone(e.target.value)}
                                        placeholder="+6791234567"
                                        disabled={!smsEnabled}
                                        className="bg-card text-foreground placeholder:text-muted-foreground"
                                    />
                                </div>
                                <div>
                                    <Label className="text-xs font-app text-muted-foreground">Message</Label>
                                    <Textarea
                                        data-testid="sms-body"
                                        value={smsBody}
                                        onChange={(e) => setSmsBody(e.target.value)}
                                        rows={3}
                                        maxLength={1600}
                                        disabled={!smsEnabled}
                                        placeholder="Bula vinaka — quick note from Bula Real Estate Fiji…"
                                        className="bg-card text-foreground placeholder:text-muted-foreground resize-none"
                                    />
                                    <div className="text-[10px] text-muted-foreground font-app mt-1 tabular-nums text-right">
                                        {charCount} chars · {segments} SMS segment{segments > 1 ? "s" : ""}
                                    </div>
                                </div>
                                <Button
                                    onClick={sendSms}
                                    disabled={sending || !smsEnabled || !smsBody.trim()}
                                    data-testid="sms-send-btn"
                                    className="w-full bg-coral hover:bg-coral-deep text-white"
                                >
                                    {sending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                                    {sending ? "Sending…" : "Send SMS"}
                                </Button>
                            </div>
                        </div>

                        {/* SMS history */}
                        {messages.length > 0 && (
                            <div data-testid="sms-history">
                                <h4 className="font-display font-bold text-sm text-foreground mb-2 flex items-center gap-2">
                                    <Hash className="h-4 w-4 text-coral" /> Outbound history ({messages.length})
                                </h4>
                                <div className="space-y-2">
                                    {messages.map((m) => (
                                        <div key={m.id} className="bg-secondary/40 dark:bg-secondary/30 border border-border rounded p-3">
                                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                                <Badge className={
                                                    m.status === "sent"
                                                        ? "bg-fiji-green/20 text-fiji-green border-fiji-green/30 text-[10px]"
                                                        : "bg-coral/20 text-coral border-coral/30 text-[10px]"
                                                }>
                                                    {m.status === "sent" ? <CheckCircle2 className="h-2.5 w-2.5 mr-1" /> : <X className="h-2.5 w-2.5 mr-1" />}
                                                    {(m.twilio_status || m.status || "").toUpperCase()}
                                                </Badge>
                                                <span className="text-[10px] text-muted-foreground font-app tabular-nums">{fmtDateTime(m.created_at)}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground font-app mb-1">
                                                <Phone className="inline h-3 w-3 mr-1" />{m.to_phone}
                                            </div>
                                            <div className="font-app text-sm text-foreground whitespace-pre-wrap">{m.body}</div>
                                            {m.error && (
                                                <div className="text-[11px] text-coral font-app mt-1.5 italic">Error: {m.error}</div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Source details timeline */}
                        <div>
                            <h4 className="font-display font-bold text-sm text-foreground mb-2 flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-coral" /> Activity timeline ({c.details.length})
                            </h4>
                            <div className="space-y-2">
                                {c.details.map((d, i) => {
                                    const m = SOURCE_META[d.source] || { label: d.source, icon: Hash, color: "text-muted-foreground" };
                                    const Icon = m.icon;
                                    return (
                                        <div key={i} className="bg-secondary/40 dark:bg-secondary/30 border border-border rounded p-3">
                                            <div className="flex items-center justify-between gap-2 mb-1.5">
                                                <span className={`font-app text-xs font-semibold flex items-center gap-1.5 ${m.color}`}>
                                                    <Icon className="h-3.5 w-3.5" /> {m.label}
                                                </span>
                                                <span className="text-[10px] text-muted-foreground font-app tabular-nums">{fmtDateTime(d.created_at)}</span>
                                            </div>
                                            <div className="text-xs text-muted-foreground font-app space-y-0.5">
                                                {d.lead_type && <div>Type: <span className="text-foreground">{d.lead_type}</span></div>}
                                                {d.status && <div>Status: <span className="text-foreground">{d.status}</span></div>}
                                                {d.business_title && <div>Business: <span className="text-foreground">{d.business_title}</span></div>}
                                                {d.message && <div className="text-foreground italic mt-1">"{d.message}"</div>}
                                                {d.purpose && <div className="text-foreground italic mt-1">Purpose: "{d.purpose}"</div>}
                                                {d.interests && d.interests.length > 0 && <div>Interests: <span className="text-foreground">{d.interests.join(", ")}</span></div>}
                                                {d.signup_source && <div>Source: <span className="text-foreground">{d.signup_source}</span></div>}
                                                {d.roles && d.roles.length > 0 && <div>Roles: <span className="text-foreground">{d.roles.join(", ")}</span></div>}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="ghost" onClick={onClose} className="font-app">Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
